import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  bulkUpdateFacilityStatus,
  createFacility,
  deleteFacility,
  listFacilities,
  updateFacility,
  updateFacilityStatus,
} from "../../../services/librariesCatalogService";

const DEFAULT_FILTERS = {
  search: "",
  status: "",
  facilityType: "",
  sortBy: "facilityName",
  sortOrder: "asc",
};

/**
 * Care Facilities list + CRUD hook (tenant-scoped via API).
 */
export default function useCareFacilities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listFacilities({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        facilityType: filters.facilityType || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
      setItems(result?.items || []);
      setPagination((p) => ({
        ...p,
        total: result?.total ?? 0,
        totalPages: result?.totalPages ?? 1,
        page: result?.page ?? p.page,
        limit: result?.limit ?? p.limit,
      }));
      setSelectedIds([]);
    } catch (err) {
      toast.error(err.message || "Failed to load care facilities");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => {
        if (f.search === searchInput) return f;
        return { ...f, search: searchInput };
      });
      setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const create = useCallback(
    async (payload) => {
      await createFacility(payload);
      toast.success("Care facility created");
      await fetchList();
    },
    [fetchList],
  );

  const update = useCallback(
    async (id, payload) => {
      await updateFacility(id, payload);
      toast.success("Care facility updated");
      await fetchList();
    },
    [fetchList],
  );

  const remove = useCallback(
    async (id) => {
      await deleteFacility(id);
      toast.success("Care facility deleted");
      await fetchList();
    },
    [fetchList],
  );

  const changeStatus = useCallback(
    async (id, status) => {
      await updateFacilityStatus(id, status);
      toast.success(`Status set to ${status}`);
      await fetchList();
    },
    [fetchList],
  );

  const bulkChangeStatus = useCallback(
    async (status) => {
      if (!selectedIds.length) {
        toast.error("Select at least one row");
        return;
      }
      const result = await bulkUpdateFacilityStatus(selectedIds, status);
      toast.success(
        `Updated ${result?.updatedCount ?? 0} facilities to ${status}`,
      );
      await fetchList();
    },
    [fetchList, selectedIds],
  );

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setFilters(DEFAULT_FILTERS);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.length === items.length) return [];
      return items.map((r) => r.id);
    });
  }, [items]);

  return {
    items,
    loading,
    filters,
    setFilters,
    searchInput,
    setSearchInput,
    pagination,
    setPagination,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearFilters,
    refresh: fetchList,
    create,
    update,
    remove,
    changeStatus,
    bulkChangeStatus,
  };
}
